import { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '@aeon-karaoke/cloud-convex';
import { MonoLabel, Toggle } from '@/component';
import type { CurrentUser } from '@/hook/useCurrentUser';
import { toResult } from '@/lib/result-adapter';

const CHANNELS = [
  {
    key: 'findableByHandle',
    title: 'Findable by handle',
    body: 'People can find you by searching for your @handle. Your profile address still works for anyone you give it to.',
  },
  {
    key: 'suggestedToCoSingers',
    title: 'Suggested to people you sang with',
    body: 'People who were in a session with you see you under "Sang with you".',
  },
] as const;

type Channel = (typeof CHANNELS)[number]['key'];

/** Settings, Profile: one switch per discovery channel. Each writes on its
 * own and flips at once, so turning a channel off takes effect immediately
 * rather than on Save. A rejected write flips the switch back and says which
 * one was not saved. An unset flag is on. */
export function DiscoverabilityRows({ user }: { user: Pick<CurrentUser, Channel> }) {
  const [error, setError] = useState<string | null>(null);
  const setDiscoverability = useMutation(api.friends.setDiscoverability).withOptimisticUpdate((store, args) => {
    const me = store.getQuery(api.users.me, {});
    if (me) store.setQuery(api.users.me, {}, { ...me, ...args });
  });

  async function change(channel: (typeof CHANNELS)[number], on: boolean) {
    setError(null);
    const result = await toResult(() => setDiscoverability({ [channel.key]: on }));
    if (!result.ok) setError(`"${channel.title}" was not saved: ${result.error.message}`);
  }

  return (
    <div className="mt-[6px] border-t border-stripeA pt-[18px]">
      <MonoLabel className="block">Discovery</MonoLabel>
      {CHANNELS.map((channel) => (
        <div key={channel.key} className="flex items-start justify-between gap-6 border-b border-stripeA py-[18px]">
          <div>
            <div className="text-[15px] font-semibold">{channel.title}</div>
            <p className="mt-1 max-w-[44ch] text-sm leading-[1.5] text-mute">{channel.body}</p>
          </div>
          <Toggle checked={user[channel.key] !== false} onChange={(on) => void change(channel, on)} label={channel.title} />
        </div>
      ))}
      {error && <p className="mt-3 text-[13px] text-down">{error}</p>}
    </div>
  );
}
